// Loads PDF bytes from a URL or a File and hands them to PdfEmbed. Heavy
// (PDFium wasm), so PdfEmbed itself is lazy-loaded.
import { lazy, Suspense, useEffect, useState } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { fetchBytes } from '@/lib/api'

import type { FieldOverlay } from '@/components/grounding/pdf-embed'
import type { ProvenanceEntry } from '@/components/grounding/provenance-types'

const PdfEmbed = lazy(() =>
  import('@/components/grounding/pdf-embed').then((m) => ({ default: m.PdfEmbed })),
)

export type PdfSource = { url: string } | { file: File } | { bytes: ArrayBuffer }

export function usePdfBytes(source: PdfSource | undefined) {
  const [bytes, setBytes] = useState<ArrayBuffer>()
  const [error, setError] = useState<string>()
  const key = source
    ? 'url' in source
      ? source.url
      : 'file' in source
        ? `${source.file.name}:${source.file.size}`
        : `bytes:${source.bytes.byteLength}`
    : ''
  useEffect(() => {
    let cancelled = false
    setBytes(undefined)
    setError(undefined)
    if (!source) return
    const load =
      'url' in source
        ? fetchBytes(source.url)
        : 'file' in source
          ? source.file.arrayBuffer()
          : Promise.resolve(source.bytes)
    load
      .then((b) => !cancelled && setBytes(b))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'load failed'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return { bytes, error, key }
}

export function PdfPane({
  source,
  name,
  citation,
  overlays,
  onOverlayClick,
  onOverlayHover,
  toolbar = true,
}: {
  source: PdfSource | undefined
  name: string
  citation?: ProvenanceEntry
  overlays?: FieldOverlay[]
  onOverlayClick?: (id: string) => void
  onOverlayHover?: (id: string | undefined) => void
  toolbar?: boolean
}) {
  const { bytes, error, key } = usePdfBytes(source)
  if (error)
    return (
      <div className="p-4 text-sm text-destructive">
        Could not load {name}: {error}
      </div>
    )
  if (!bytes)
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Spinner />
      </div>
    )
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <Spinner />
        </div>
      }
    >
      <PdfEmbed
        bytes={bytes}
        documentId={key}
        name={name}
        citation={citation}
        overlays={overlays}
        onOverlayClick={onOverlayClick}
        onOverlayHover={onOverlayHover}
        showToolbar={toolbar}
      />
    </Suspense>
  )
}
